// packages/web-ui/src/panels/chat-panel/SessionHeader.tsx
//
// Session header bar (40 px, sticky-top inside `<ChatPanel>`).
//
// Left segment: editable title (double-click → input), provider chip.
// Right segment: regen-last + overflow `⋯` menu with Rename / Export
// (JSON|Markdown) / New from this point (W2-S8 stub) / Delete session.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
} from 'react';
import {
  RotateCw,
  MoreHorizontal,
} from 'lucide-react';
import { useOpenResource } from '../../hooks/useOpenResource.js';

const TITLE_MAX_LENGTH = 80;

export interface SessionHeaderProps {
  sessionId: string;
  /** May be empty — falls back to "New conversation". */
  title: string;
  providerName: string;
  tokensUsed: number | null;
  /** True while the assistant is streaming OR a user message is in flight. */
  isBusy: boolean;
  onRename: (sessionId: string, title: string) => void;
  onRegenerateLast: (sessionId: string) => void;
  onExport: (sessionId: string, format: 'json' | 'markdown') => void;
  onDelete: (sessionId: string) => void;
  /** Test seam — overrides `useOpenResource()`. */
  openResource?: (destination: 'settings', section: string) => void;
}

/** Format token counts as `12 376 tok` per UX §6.1.2 example. */
function formatTokens(value: number | null): string {
  if (value === null) return '— tok';
  return `${value.toLocaleString('en-US').replace(/,/g, ' ')} tok`;
}

export function SessionHeader(props: SessionHeaderProps): JSX.Element {
  const fallbackOpen = useOpenResource();
  const openResource =
    props.openResource ?? ((dest: 'settings', section: string) => fallbackOpen(dest, section));

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(props.title);
  const [menuOpen, setMenuOpen] = useState(false);
  const [exportSubOpen, setExportSubOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Focus + select-all when entering edit mode.
  useEffect(() => {
    if (editing && inputRef.current !== null) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Close the menu on outside click / Esc.
  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent): void {
      if (menuRef.current?.contains(e.target as Node)) return;
      if (triggerRef.current?.contains(e.target as Node)) return;
      setMenuOpen(false);
      setExportSubOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent): void {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setExportSubOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const beginEdit = useCallback(() => {
    setDraft(props.title);
    setEditing(true);
  }, [props.title]);

  const commitEdit = useCallback(() => {
    const next = draft.trim().slice(0, TITLE_MAX_LENGTH);
    setEditing(false);
    if (next !== props.title && next.length > 0) {
      props.onRename(props.sessionId, next);
    }
  }, [draft, props]);

  const cancelEdit = useCallback(() => {
    setDraft(props.title);
    setEditing(false);
  }, [props.title]);

  const onTitleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
    },
    [commitEdit, cancelEdit],
  );

  const displayTitle = props.title.length === 0 ? 'New conversation' : props.title;

  // Shift+F10 opens the menu, matching W2-S4 context-menu pattern.
  const onTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'F10' && e.shiftKey) {
        e.preventDefault();
        setMenuOpen((open) => !open);
      }
    },
    [],
  );

  return (
    <header
      role="banner"
      aria-label="Chat session header"
      data-testid="session-header"
      className="sticky top-0 z-10 flex h-10 items-center justify-between gap-3 border-b border-soft bg-cream-100 px-3 py-2"
    >
      <div className="flex min-w-0 items-center gap-3">
        {editing ? (
          <input
            ref={inputRef}
            data-testid="session-title-input"
            aria-label="Session title"
            type="text"
            value={draft}
            maxLength={TITLE_MAX_LENGTH}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onTitleKeyDown}
            onBlur={commitEdit}
            className="max-w-[240px] truncate border border-taupe-500 bg-cream-50 px-1 py-0.5 text-sm"
          />
        ) : (
          <button
            type="button"
            data-testid="session-title-button"
            aria-label="Edit session title"
            onDoubleClick={beginEdit}
            className="max-w-[240px] truncate text-left text-sm font-medium text-taupe-700 hover:text-taupe-900"
          >
            {displayTitle}
          </button>
        )}
        <button
          type="button"
          data-testid="session-provider-chip"
          aria-label={`Provider: ${props.providerName}; ${props.tokensUsed ?? 'unknown'} tokens used`}
          onClick={() => openResource('settings', 'providers')}
          className="rounded-full bg-cream-200 px-2 py-0.5 text-xs text-taupe-700 hover:bg-cream-300"
        >
          {props.providerName} · {formatTokens(props.tokensUsed)}
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="session-regen-button"
          aria-label="Regenerate last response"
          title="Regenerate last response"
          disabled={props.isBusy}
          onClick={() => props.onRegenerateLast(props.sessionId)}
          className={`inline-flex h-6 w-6 items-center justify-center rounded text-taupe-700 hover:bg-cream-200 ${
            props.isBusy ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          <RotateCw size={16} />
        </button>
        <div className="relative">
          <button
            ref={triggerRef}
            type="button"
            data-testid="session-overflow-trigger"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Session menu"
            onClick={() => setMenuOpen((o) => !o)}
            onKeyDown={onTriggerKeyDown}
            className="inline-flex h-6 w-6 items-center justify-center rounded text-taupe-700 hover:bg-cream-200"
          >
            <MoreHorizontal size={16} />
          </button>
          {menuOpen ? (
            <div
              ref={menuRef}
              role="menu"
              data-testid="session-overflow-menu"
              className="border-taupe-500 bg-cream-50 absolute right-0 top-full z-20 mt-1 min-w-[180px] rounded-none border py-1 text-sm"
            >
              <button
                type="button"
                role="menuitem"
                data-testid="menu-rename"
                onClick={() => {
                  setMenuOpen(false);
                  beginEdit();
                }}
                className="block w-full px-3 py-1 text-left hover:bg-cream-200"
              >
                Rename
              </button>
              <div
                onMouseEnter={() => setExportSubOpen(true)}
                onMouseLeave={() => setExportSubOpen(false)}
                className="relative"
              >
                <button
                  type="button"
                  role="menuitem"
                  aria-haspopup="menu"
                  aria-expanded={exportSubOpen}
                  data-testid="menu-export"
                  onClick={() => setExportSubOpen((o) => !o)}
                  className="block w-full px-3 py-1 text-left hover:bg-cream-200"
                >
                  Export transcript ›
                </button>
                {exportSubOpen ? (
                  <div
                    role="menu"
                    data-testid="menu-export-sub"
                    className="border-taupe-500 bg-cream-50 absolute left-full top-0 z-30 -ml-px min-w-[140px] rounded-none border py-1"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      data-testid="menu-export-json"
                      onClick={() => {
                        setMenuOpen(false);
                        setExportSubOpen(false);
                        props.onExport(props.sessionId, 'json');
                      }}
                      className="block w-full px-3 py-1 text-left hover:bg-cream-200"
                    >
                      JSON
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      data-testid="menu-export-markdown"
                      onClick={() => {
                        setMenuOpen(false);
                        setExportSubOpen(false);
                        props.onExport(props.sessionId, 'markdown');
                      }}
                      className="block w-full px-3 py-1 text-left hover:bg-cream-200"
                    >
                      Markdown
                    </button>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                role="menuitem"
                aria-disabled={true}
                data-testid="menu-fork"
                onClick={() => {
                  console.warn('TODO(W2-S8): fork-session not wired');
                }}
                className="block w-full cursor-not-allowed px-3 py-1 text-left text-taupe-300"
              >
                New from this point (W2-S8)
              </button>
              <button
                type="button"
                role="menuitem"
                data-testid="menu-delete"
                onClick={() => {
                  setMenuOpen(false);
                  setDeleteDialogOpen(true);
                }}
                className="block w-full px-3 py-1 text-left text-accent-red hover:bg-cream-200"
              >
                Delete
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {deleteDialogOpen ? (
        <ConfirmDialog
          testId="delete-dialog"
          title="Delete session?"
          message="This cannot be undone."
          confirmLabel="Delete"
          onCancel={() => setDeleteDialogOpen(false)}
          onConfirm={() => {
            setDeleteDialogOpen(false);
            props.onDelete(props.sessionId);
          }}
        />
      ) : null}
    </header>
  );
}

interface ConfirmDialogProps {
  testId: string;
  title: string;
  message: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmDialog(props: ConfirmDialogProps): JSX.Element {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      data-testid={props.testId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
    >
      <div className="border-taupe-500 bg-cream-50 rounded-none border p-5">
        <h2 className="mb-2 text-sm font-semibold text-taupe-900">{props.title}</h2>
        <p className="mb-3 text-sm text-taupe-700">{props.message}</p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid={`${props.testId}-cancel`}
            onClick={props.onCancel}
            className="rounded border border-taupe-500 px-2 py-0.5 text-sm hover:bg-cream-200"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid={`${props.testId}-confirm`}
            onClick={props.onConfirm}
            className="rounded bg-accent-red px-2 py-0.5 text-sm text-white hover:opacity-90"
          >
            {props.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
